import { DiaFestivo } from '@schemas/festivos.schema';
import { SaldoVacaciones } from '@schemas/vacaciones.schema';

/**
 * Convierte un Date a 'YYYY-MM-DD' en hora local (sin desfase UTC).
 */
export function toISODateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parsea 'YYYY-MM-DD' (mock viejo) o 'YYYY-MM-DDTHH:mm:ss[.fff]' (timestamp
 * ISO del backend real) como fecha local — evita el desfase UTC de
 * `new Date(str)` cuando el string trae timezone Z.
 *
 * Antes este helper hacía `dateStr.split('-')` directamente, lo que con el
 * formato ISO daba `[2026, 5, NaN]` y producía Invalid Date — eso ocultaba
 * los rangos en el calendario de FullCalendar.
 */
export function parseLocalDate(dateStr: string): Date {
  // Toma solo la parte de fecha (antes de la 'T' si la hay).
  const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Misma fecha a las 00:00 locales.
 *
 * Necesario al comparar una fecha "de reloj" contra rangos: `parseLocalDate`
 * devuelve medianoche, así que un `new Date()` con la hora actual queda POR
 * ENCIMA del fin del rango y una ausencia que termina hoy se pierde en el
 * `sel <= fin`.
 */
export function inicioDelDia(fecha: Date): Date {
  const d = new Date(fecha);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Calcula días hábiles entre dos fechas (inclusive), excluyendo:
 *  - Sábados y domingos
 *  - Días festivos del array proporcionado
 *
 * @param inicio   Fecha de inicio (inclusiva)
 * @param fin      Fecha de fin (inclusiva)
 * @param festivos Lista de festivos a excluir (por defecto vacía → solo excluye fines de semana)
 */
export function calcularDiasHabiles(inicio: Date, fin: Date, festivos: DiaFestivo[] = []): number {
  const festivosSet = new Set(festivos.map((f) => f.fecha));
  let dias = 0;
  const actual = new Date(inicio);
  while (actual <= fin) {
    const dow = actual.getDay();
    if (dow !== 0 && dow !== 6 && !festivosSet.has(toISODateString(actual))) {
      dias++;
    }
    actual.setDate(actual.getDate() + 1);
  }
  return dias;
}

/**
 * Devuelve true si la fecha cae en algún festivo de la lista.
 */
export function esFestivo(fecha: Date, festivos: DiaFestivo[]): boolean {
  const str = toISODateString(fecha);
  return festivos.some((f) => f.fecha === str);
}

/**
 * Devuelve el nombre del festivo si la fecha coincide, undefined si no.
 */
export function getNombreFestivo(fecha: Date, festivos: DiaFestivo[]): string | undefined {
  const str = toISODateString(fecha);
  return festivos.find((f) => f.fecha === str)?.nombre;
}

/** Reparto FIFO de los días de un rango entre los ciclos disponibles. */
export interface DistribucionFIFO {
  /** Días que salen del ciclo anterior (el que vence primero). */
  delAnterior: number;
  /** Días que salen del ciclo en curso. */
  delActual: number;
  /** Días que quedan a deber y se compensarán con lo que se genere después. */
  negativo: number;
}

/**
 * Reparte los días de un rango entre los ciclos del saldo, gastando primero el
 * que vence antes (FIFO). Vive aquí y no en un modal porque lo consumen tanto
 * el alta como la edición: el empleado necesita ver el mismo impacto cuando
 * pide días y cuando cambia las fechas de una solicitud existente.
 *
 * Los días del ciclo anterior solo cuentan hasta su fecha de vencimiento: si el
 * rango la cruza, únicamente el tramo previo se descuenta de ahí.
 */
export function calcularDistribucionFIFO(
  saldo: SaldoVacaciones | null,
  rango: Date[] | null,
  festivos: DiaFestivo[],
): DistribucionFIFO | null {
  if (!saldo || !rango || rango.length !== 2) return null;

  const [inicio, fin] = rango;
  const dias = calcularDiasHabiles(inicio, fin, festivos);
  if (dias === 0) return null;

  const anterior = saldo.periodos.find((p) => !p.enCurso && !p.vencido && p.diasDisponibles > 0);
  const actual = saldo.periodos.find((p) => p.enCurso);

  let delAnterior = 0;
  if (anterior && anterior.diasDisponibles > 0) {
    const venc = parseLocalDate(anterior.fechaVencimiento);
    if (fin <= venc) {
      delAnterior = Math.min(dias, anterior.diasDisponibles);
    } else if (inicio <= venc) {
      delAnterior = Math.min(calcularDiasHabiles(inicio, venc, festivos), anterior.diasDisponibles);
    }
  }

  const restante = dias - delAnterior;
  const delActual = Math.min(restante, actual?.diasDisponibles ?? 0);

  return { delAnterior, delActual, negativo: Math.max(0, restante - delActual) };
}

/**
 * Permite confirmar un rango de fechas escrito manualmente en un nz-range-picker.
 *
 * Comportamiento:
 *  - Si el usuario está en el primer input y la fecha es válida → mueve el foco al segundo.
 *  - Si ambos inputs tienen fechas válidas y el rango es coherente → lo confirma y cierra.
 *  - En cualquier otro caso → simplemente quita el foco del input activo.
 *
 * @param applyFn Función que recibe el rango resultante (llamar a `signal.set`)
 * @param event   Evento keydown.enter capturado en el elemento nz-range-picker
 */
export function commitRangeOnEnter(applyFn: (dates: Date[] | null) => void, event: Event): void {
  if (event instanceof KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  const host = event.currentTarget as HTMLElement;
  const inputs = Array.from(host.querySelectorAll('input')) as HTMLInputElement[];
  if (inputs.length < 2) return;

  const activeInput = event.target as HTMLInputElement;
  const isFirst = inputs.indexOf(activeInput) === 0;

  const parseYMD = (raw: string): Date | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw.trim());
    if (!m) return null;
    const [, y, mo, d] = [0, ...m.slice(1).map(Number)] as number[];
    const date = new Date(y, mo - 1, d);
    return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d
      ? date
      : null;
  };

  const start = parseYMD(inputs[0].value);
  const end = parseYMD(inputs[1].value);

  if (start && end && start <= end) {
    applyFn([start, end]);
    inputs.forEach((i) => i.blur());
  } else if (isFirst && start) {
    inputs[1].focus();
    inputs[1].select();
  } else {
    activeInput.blur();
  }
}
